/**
 * Platform Improvement Recommendations (R4.2 / LA-010).
 *
 * First REAL Adam-side consumer of learning-hub fleet analytics. Closes
 * the "write-only graveyard" finding from D1's audit by surfacing
 * recommendations the platform team can act on.
 *
 * Reads `/api/learning/recommendations` (Adam proxies to
 * learning-hub `/api/recommendations`). Read-only surface — promoting
 * a recommendation to action is operator-driven outside this UI.
 */
import { useQuery } from "@tanstack/react-query";

import {
  PageHeader,
  Card,
  SectionHeader,
  Badge,
  Spinner,
  QueryError,
  EmptyState,
} from "../components";
import { usePolling } from "../hooks/usePolling";
import { apiGet } from "../lib/api";

type RecommendationKind =
  | "high_undo_rate"
  | "high_alpha_convergence"
  | "proposed_macro";

type Leverage = "low" | "medium" | "high";

interface Recommendation {
  id: string;
  kind: RecommendationKind;
  summary: string;
  detail: string;
  leverage: Leverage;
  score: number;
  contributingDeployments: number;
  evidence: Record<string, unknown>;
}

interface RecommendationsResponse {
  recommendations: Recommendation[];
  timestamp: number;
  params: Record<string, number>;
}

const KIND_LABELS: Record<RecommendationKind, string> = {
  high_undo_rate: "High Undo Rate",
  high_alpha_convergence: "Converged Knob",
  proposed_macro: "Proposed Macro",
};

const LEVERAGE_VARIANT: Record<Leverage, "danger" | "warning" | "info"> = {
  high: "danger",
  medium: "warning",
  low: "info",
};

export function PlatformImprovementRecommendations(): JSX.Element {
  const { data, isLoading, error, refetch } = useQuery<RecommendationsResponse>({
    queryKey: ["learning", "recommendations"],
    queryFn: () => apiGet<RecommendationsResponse>("/api/learning/recommendations"),
  });

  usePolling(refetch, 60_000);

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Platform Improvement Recommendations"
          description="Fleet-aggregated learning signals worth acting on."
        />
        <Spinner />
      </div>
    );
  }
  if (error) {
    return (
      <div>
        <PageHeader
          title="Platform Improvement Recommendations"
          description="Fleet-aggregated learning signals worth acting on."
        />
        <QueryError error={error as Error} retry={refetch} />
      </div>
    );
  }
  const recommendations = data?.recommendations ?? [];
  return (
    <div>
      <PageHeader
        title="Platform Improvement Recommendations"
        description="Fleet-aggregated learning signals worth acting on. Read-only surface — promoting a recommendation to action is operator-driven."
      />
      {recommendations.length === 0 ? (
        <EmptyState
          title="No recommendations"
          description="Learning-hub aggregation has no fleet signals strong enough to surface a recommendation yet. Recommendations appear when episode_summaries, aggregated_vectors, or global_macros tables accumulate sufficient evidence."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {recommendations.map((rec) => (
            <RecommendationCard key={rec.id} rec={rec} />
          ))}
        </div>
      )}
    </div>
  );
}

function RecommendationCard({ rec }: { rec: Recommendation }): JSX.Element {
  return (
    <Card>
      <SectionHeader
        title={KIND_LABELS[rec.kind]}
        accessory={
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <Badge variant={LEVERAGE_VARIANT[rec.leverage]}>{rec.leverage} leverage</Badge>
            <Badge variant="info">{rec.contributingDeployments} deployments</Badge>
          </div>
        }
      />
      <p style={{ marginTop: "0.5rem", fontWeight: 500 }}>{rec.summary}</p>
      <p style={{ marginTop: "0.5rem", color: "var(--c-text-secondary)", whiteSpace: "pre-line" }}>
        {rec.detail}
      </p>
      <details style={{ marginTop: "0.75rem" }}>
        <summary style={{ cursor: "pointer", color: "var(--c-text-secondary)" }}>Evidence</summary>
        <pre
          style={{
            marginTop: "0.5rem",
            padding: "0.5rem",
            background: "var(--c-bg-elev-1)",
            borderRadius: "4px",
            fontSize: "0.85em",
            overflowX: "auto",
          }}
        >
          {JSON.stringify(rec.evidence, null, 2)}
        </pre>
      </details>
    </Card>
  );
}
